import {
  CustomObject,
  EnhancedFilterCriteria,
  Filter,
} from "../interfaces/findFleetInterfaces";
import { DependencyList, EffectCallback, useEffect, useRef } from "react";
import {
  filterSelectionsType,
  Query,
  Result,
} from "../interfaces/queryBuilderInterfaces";
import { FlatFilters, KeyMap } from "../interfaces/reviewSelectionInterfaces";

export const convertToId = (label: string) => {
  return String(label)
    ?.toLowerCase()
    .replace(/[^a-z]\s/g, "")
    .replaceAll(" ", "-");
};

export function isNull(value: string | any[] | null) {
  return typeof value == "undefined" || value == null || value == "undefined";
}

export function isEmpty(value: string | any[]) {
  return (
    isNull(value) ||
    (typeof value == "string" && value.length == 0) ||
    (typeof value == "object" && Object.keys(value).length == 0)
  );
}

export function isObjectEmpty(obj: Record<string, any>): boolean {
  if (typeof obj === "object") {
    for (const key in obj) {
      if (!isEmpty(obj[key])) {
        return false;
      }
    }
  }
  return true;
}

export function emptyKeys(
  obj: Record<string, any>,
  valueKey: string,
): string[] {
  let keys: string[] = [];
  if (typeof obj === "object") {
    for (const key in obj) {
      if (!isEmpty(obj[key])) {
        if (obj[key][valueKey]?.length === 0) keys.push(key);
      }
    }
  }
  return keys;
}

export function useUpdateEffect(fn: EffectCallback, deps: DependencyList) {
  const didMount = useRef(false);
  useEffect(() => {
    if (didMount.current) fn();
    else didMount.current = true;
  }, deps);
}
const cleanedValues = (values: any) =>
  values.map((value: string) => {
    const parts = value.split(".[");
    return parts[parts.length - 1].replace("]", "");
  });
export const convertInputToEnhancedFilterCriteria = (
  filter: Record<string, { values: string[]; filterType: string }>,
  availableFilters: { [key: string]: Filter[] },
  levels: any,
): CustomObject => {
  const enhancedFilterCriteria: any = {};
  const fleetData: any = {};

  for (const key in filter) {
    const allValues = availableFilters[key];
    const selectedValues = filter[key].values;
    let include: boolean = false;
    const excludedValues: string[] = [];
    if (allValues.length !== selectedValues.length) {
      include =
        selectedValues.length < allValues.length - selectedValues.length
          ? true
          : false;

      if (!include) {
        allValues.forEach((value: any) => {
          if (!selectedValues.includes(value.name)) {
            excludedValues.push(value.name);
          }
        });
      }
    }
    const values = include ? selectedValues : excludedValues;
    enhancedFilterCriteria[key] = [
      {
        filterCategory: "Selection",
        criteria: [
          {
            condition: "in",
            values: values.map((value) => ({
              selType: "None",
              value: value,
              namedSet: false,
            })),
            incExc: include,
            joiner: "NOOP",
          },
        ],
      },
    ];
    const level = levels.find(
      (level: { uniqueName: any }) => level.uniqueName === key,
    );
    const selectedDataValues = cleanedValues(filter[key].values);
    const excludedDataValues: string[] = cleanedValues(excludedValues);
    const dataValues = include ? selectedDataValues : excludedDataValues;
    fleetData[level.id] = {
      values: dataValues,
      type: include ? "in" : "not in",
    };
  }
  return { enhancedFilterCriteria, fleetData };
};

export const convertZipFilterToEnhancedFilterCriteria = (
  enhancedFilterCriteria: any,
  zips: string[],
  levels: any,
  fleetData: any,
): void => {
  if (zips && zips.length > 0) {
    const filter = [
      {
        filterCategory: "Selection",
        criteria: [
          {
            condition: "in",
            values: zips.map((value) => ({
              selType: "None",
              value: value,
              namedSet: false,
            })),
            incExc: true,
            joiner: "NOOP",
          },
        ],
      },
    ];
    const level = levels.find((level: { name: any }) => level.name === "Zip");
    enhancedFilterCriteria[level.uniqueName] = filter;

    fleetData[level.id] = {
      values: zips,
      type: "in",
    };
  }
};

export const addFleetSearchToEnhancedFilterCriteria = (
  enhancedFilterCriteria: any,
  seachText: string,
  levels: any,
  fleetData: any,
): void => {
  if (seachText && seachText.length > 0) {
    const filter = [
      {
        filterCategory: "WildCard",
        criteria: [
          {
            condition: "Contains",
            values: [
              {
                selType: "None",
                value: seachText,
              },
            ],
            incExc: true,
            joiner: "NOOP",
          },
        ],
      },
    ];

    const level = levels.find((level: { name: any }) => level.name === "Name");

    enhancedFilterCriteria[level.uniqueName] = filter;

    fleetData[level.id] = {
      values: [seachText],
      type: "like",
    };
  } else {
    // delete the fleet search filter if the search text is empty
    const level = levels.find((level: { name: any }) => level.name === "Name");
    if (level && enhancedFilterCriteria[level.uniqueName]) {
      delete enhancedFilterCriteria[level.uniqueName];
    }
    if (level && fleetData[level.id]) {
      delete fleetData[level.id];
    }
    // remove the fleet search filter from the fleetData object
  }
};

export const convertFilterToEnhancedFilterCriteria = (
  filterValues: string[],
): any => {
  if (filterValues && filterValues.length > 0) {
    const filter = [
      {
        filterCategory: "Selection",
        criteria: [
          {
            condition: "in",
            values: filterValues.map((value) => ({
              selType: "None",
              value: value,
              namedSet: false,
            })),
            incExc: true,
            joiner: "NOOP",
          },
        ],
      },
    ];
    return filter;
  }
};

export const aggregateFleetResult = (result: Result | null) => {
  if (result) {
    const map = new Map();
    result.result.rows.forEach((row: any) => {
      const key = row["Name"] + "_" + row["Location"] + row["Zip"];
      const count = parseInt(row["[Vehicle Count]"]);
      const GVW = row["GVW"];
      const Fuel = row["Fuel"];
      const make = row["Make"];
      if (!map.has(key)) {
        const clonedRow = structuredClone(row); // Clone the row object
        map.set(key, clonedRow);
        ((clonedRow.chartData = {}),
          (clonedRow.chartData.Make = {}),
          (clonedRow.chartData.GVW = {}),
          (clonedRow.chartData.Fuel = {}));
        clonedRow.chartData.Make[make] = count;
        clonedRow.chartData.GVW[GVW] = count;
        clonedRow.chartData.Fuel[Fuel] = count;
        clonedRow["[Vehicle Count]"] = count;
      } else {
        const data = map.get(key);
        data["[Vehicle Count]"] = data["[Vehicle Count]"] + count;
        if (data.chartData.Make[make]) {
          data.chartData.Make[make] = data.chartData.Make[make] + count;
        } else {
          data.chartData.Make[make] = count;
        }
        if (data.chartData.GVW[GVW]) {
          data.chartData.GVW[GVW] = data.chartData.GVW[GVW] + count;
        } else {
          data.chartData.GVW[GVW] = count;
        }
        if (data.chartData.Fuel[Fuel]) {
          data.chartData.Fuel[Fuel] = data.chartData.Fuel[Fuel] + count;
        } else {
          data.chartData.Fuel[Fuel] = count;
        }
      }
    });
    return map.values();
  }
};

export const attributeToName = (input: string) => {
  const spacedString = input.replace(/([A-Z])/g, " $1");
  const capitalizedString = spacedString.toUpperCase();
  return capitalizedString.trim();
};

export const removeAttributesHasObject = (
  obj: CustomObject,
  removeAttribute: string[],
) => {
  for (const key in obj) {
    if (removeAttribute.includes(key)) {
      delete obj[key];
    } else if (typeof obj[key] === "object" && obj[key] !== null) {
      delete obj[key];
    }
  }
};
export const removeAllOtherAttributes = (
  obj: CustomObject,
  removeAttribute: string[],
) => {
  for (const key in obj) {
    if (!removeAttribute.includes(key)) {
      delete obj[key];
    }
  }
};
export const objectToArray = (obj: CustomObject) => {
  let data = Object.entries(obj).map(([asset, amount]) => ({
    asset,
    amount: Number(amount),
  }));
  return data.sort((a, b) => b.amount - a.amount);
};

export const generateColors = (numColors: number) => {
  const colors: string[] = [];
  for (let i = 0; i < numColors; i++) {
    const randomColor = Math.floor(Math.random() * 16777215).toString(16);
    colors.push(`#${randomColor.padStart(6, "0")}`);
  }
  return colors;
};

export function hasMatch(value: any, checkArray: any[]) {
  return checkArray.some((item) => value.includes(item));
}

export const arraysEqual = (arr1: any[], arr2: any[]) => {
  if (arr1.length !== arr2.length) {
    return false; // Arrays of different lengths cannot be equal
  }
  return arr1.every((value, index) => value === arr2[index]);
};

export const formatNumberWithCommas = (value: number) => {
  if (value) {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  } else {
    return value;
  }
};

export const convertSelectionIntoFilter = (item: filterSelectionsType): any => {
  const filter: any = [];
  if (item.selectedFilters && item.selectedFilters.length > 0) {
    const regularSelections = item.selectedFilters.filter(
      (value) => !value.isNamedSet,
    );
    const namedSetSelections = item.selectedFilters.filter(
      (value) => value.isNamedSet,
    );

    const criteria: any[] = [];

    if (regularSelections.length > 0) {
      criteria.push({
        condition: "in",
        values: regularSelections.map((value) => ({
          selType: value.selType,
          value: value.name,
          namedSet: false,
        })),
        incExc: !item.exclude,
        joiner: "NOOP",
      });
    }

    if (namedSetSelections.length > 0) {
      criteria.push({
        condition: "in",
        values: namedSetSelections.map((value) => ({
          selType: value.selType,
          value: value.name,
          namedSet: true,
        })),
        incExc: !item.exclude,
        joiner: "NOOP",
      });
    }

    const filterCriteria = {
      filterCategory: "Selection",
      isAllSelected: item?.isAllSelected || false,
      criteria,
    };
    filter.push(filterCriteria);
  }
  if (item.condition && item.condition.type && item.condition.value) {
    const conditionCriteria = {
      filterCategory: "WildCard",
      criteria: [
        {
          condition: item.condition.type,
          values: [
            {
              selType: "None",
              value: item.condition.value,
            },
          ],
          incExc: !item.condition.exclude,
          joiner: "NOOP",
        },
      ],
    };
    filter.push(conditionCriteria);
  }
  if (
    item.formula &&
    item.formula.filter1Type &&
    item.formula.filter1Value &&
    !item.formula.toggleType
  ) {
    const formulaCriteria = {
      filterCategory: "Logical",
      criteria: [
        {
          condition: item.formula.filter1Type,
          values: [
            {
              selType: "None",
              value: item.formula.filter1Value,
            },
          ],
          incExc: true,
          joiner: "NOOP",
        },
      ],
    };
    filter.push(formulaCriteria);
  }
  if (
    item.formula &&
    item.formula.filter1Type &&
    item.formula.filter1Value &&
    item.formula.toggleType
  ) {
    const formulaCriteria = {
      filterCategory: "Logical",
      criteria: [
        {
          condition: item.formula.filter1Type,
          values: [
            {
              selType: "None",
              value: item.formula.filter1Value,
            },
          ],
          incExc: true,
          joiner: item.formula.radioOption,
        },
        {
          condition: item.formula.filter2Type,
          values: [
            {
              selType: "None",
              value: item.formula.filter2Value,
            },
          ],
          incExc: true,
          joiner: "NOOP",
        },
      ],
    };
    filter.push(formulaCriteria);
  }
  return filter;
};

export const convertFilterCriteriaToSelection = (item: any) => {
  let filter = null;
  item.forEach((filterCriteria: any) => {
    if (filterCriteria.filterCategory === "Selection") {
      const selectedFilters = (filterCriteria.criteria || []).flatMap(
        (criteriaItem: any) =>
          (criteriaItem?.values || []).map(
            (v: { value: string; selType?: string; namedSet?: boolean }) => {
              return {
                name: v.value,
                altCaption: v.value,
                selType: v.selType,
                isNamedSet: v.namedSet,
              };
            },
          ),
      );
      const exclude = !(filterCriteria.criteria?.[0]?.incExc ?? true);
      filter = { selectedFilters, exclude };
    }
  });
  return filter;
};

export const convertFilterCriteriaToCondition = (item: any) => {
  let filter = null;
  item.forEach((filterCriteria: any) => {
    if (filterCriteria.filterCategory === "WildCard") {
      const type = filterCriteria.criteria[0].condition;
      const value = filterCriteria.criteria[0].values[0].value;
      const exclude = !filterCriteria.criteria[0].incExc;
      filter = { type, value, exclude };
    }
  });
  return filter;
};

export const convertFilterCriteriaToFormula = (item: any) => {
  let filter = null;
  item.forEach((filterCriteria: any) => {
    if (filterCriteria.filterCategory === "Logical") {
      const criteria = filterCriteria.criteria;
      if (criteria.length === 1) {
        const filter1Type = criteria[0].condition;
        const filter1Value = criteria[0].values[0].value;
        filter = {
          toggleType: false,
          filter1Type,
          filter1Value,
          radioOption: "AND",
          filter2Type: "",
          filter2Value: "",
        };
      } else if (criteria.length === 2) {
        const filter1Type = criteria[0].condition;
        const filter1Value = criteria[0].values[0].value;
        const radioOption = criteria[0].joiner;
        const filter2Type = criteria[1].condition;
        const filter2Value = criteria[1].values[0].value;
        filter = {
          toggleType: true,
          filter1Type,
          filter1Value,
          radioOption,
          filter2Type,
          filter2Value,
        };
      }
    }
  });
  return filter;
};

export const getTypeFromValue = (value: string) => {
  switch (value) {
    case "EqualTo":
      return "=";
    case "NotEqualTo":
      return "!=";
    case "LessThan":
      return "<";
    case "LessThanOrEqualTo":
      return "<=";
    case "GreaterThan":
      return ">";
    case "GreaterThanOrEqualTo":
      return ">=";
    default:
      return "=";
  }
};

const formatUSPhoneNumber = (value: string) => {
  const phoneNumber = value.replace(/[^\d]/g, ""); // Remove all non-digit characters
  const phoneNumberLength = phoneNumber.length;

  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7)
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

export const formatPhoneNumber = (value: string) => {
  if (!value) {
    return value;
  }
  // check if phone number starts with + and separate it with number
  const isInternational = value.startsWith("+");
  if (isInternational) {
    // Input sample is +19999999999, if input is greater than 10 phone number will be last 10 digits
    // extract country code and rest of number
    const countryCode = value.slice(1, value.length - 10);
    const restOfNumber = value.slice(value.length - 10);
    return `+${countryCode} ${formatUSPhoneNumber(restOfNumber)}`;
  } else {
    return formatUSPhoneNumber(value);
  }
};

export const getLastBrackateValue = (str: string) => {
  if (typeof str !== "string") return "";

  const matches = str.match(/\[(.*?)\]/g);
  if (!matches || matches.length === 0) return "";

  const last = matches.pop();
  if (!last) return "";
  return last.replace(/^\[|\]$/g, "").toUpperCase();
};

export const getFiltersKeyValue = (
  input: EnhancedFilterCriteria,
  axesWithAllSelected: any,
  timePeriodsData: any,
  timePeriodsNode: any,
): {
  flatFilter: FlatFilters;
  keyMap: KeyMap;
  incExcMap: Record<string, boolean>;
  isAllSelectedMap: Record<string, boolean>;
} => {
  const keyMap: KeyMap = {};
  const flatFilter: FlatFilters = {};
  const incExcMap: Record<string, boolean> = {};
  const isAllSelectedMap: Record<string, boolean> = {};

  Object.entries(input).forEach(([fullKey, items]) => {
    const parts = fullKey.split(".");
    const category = parts[parts.length - 1];
    if (!category) return;

    if (axesWithAllSelected.includes(fullKey)) {
      isAllSelectedMap[category] = true;
    }

    keyMap[category] = fullKey;
    if (!flatFilter[category]) flatFilter[category] = [];

    items?.forEach((block) => {
      if (block.filterCategory !== "Selection") return;
      if (block.isAllSelected) return;

      const criteriaList = block.criteria || [];
      criteriaList.forEach((criteria: any, index: number) => {
        if (!Array.isArray(criteria?.values)) return;

        // Store include/exclude flag once per category
        if (index === 0 && typeof incExcMap[category] === "undefined") {
          incExcMap[category] = criteria.incExc ?? true;
        }

        criteria.values.forEach((v: any) => {
          if (v.namedSet) {
            isAllSelectedMap[category] = false;
          }
          if (!v?.value) return;

          const isTimePeriodCategory =
            fullKey === (timePeriodsNode?.length > 0 ? timePeriodsNode[0] : "");

          let displayValue = v.value;
          if (
            isTimePeriodCategory &&
            Array.isArray(timePeriodsData) &&
            timePeriodsData.length > 0
          ) {
            const timePeriodMatch = timePeriodsData.find(
              (tp) => tp?.name === v.value
            );
            if (timePeriodMatch) {
              displayValue = timePeriodMatch.altCaption ?? v.value;
            }
          }

          flatFilter[category].push({
            value: displayValue,
            selType: v.selType || "None",
            namedSet: v.namedSet || false,
          });
        });
      });
    });

    flatFilter[category] = flatFilter[category].filter(
      (item, index, arr) =>
        arr.findIndex(
          (t) =>
            t.value === item.value &&
            t.selType === item.selType &&
            t.namedSet === item.namedSet
        ) === index
    );
  });

  return { flatFilter, keyMap, incExcMap, isAllSelectedMap };
};

export const buildEnhancedFilterCriteria = (
  filterData: FlatFilters,
  keyMap: KeyMap,
  incExcMap: Record<string, boolean>,
  timePeriodsData: any[],
  timePeriodsNode: any,
): EnhancedFilterCriteria => {
  const output: EnhancedFilterCriteria = {};

  Object.entries(filterData).forEach(([category, values]) => {
    const fullKey = keyMap[category];
    if (!fullKey) return;
    if (!values.length) return;

    if (!output[fullKey]) output[fullKey] = [];

    const incExc = incExcMap[category] ?? true;

    const isTimePeriodCategory =
      fullKey === (timePeriodsNode?.length > 0 ? timePeriodsNode[0] : "");

    // Separate regular selections from named set selections
    const regularSelections = values.filter((v) => !v.namedSet);
    const namedSetSelections = values.filter((v) => v.namedSet);

    const criteria: any[] = [];

    // Add regular selections criteria
    if (regularSelections.length > 0) {
      criteria.push({
        condition: "in",
        incExc: incExc,
        joiner: "NOOP",
        values: regularSelections.map((v) => {
          let originalValue = v.value;

          if (isTimePeriodCategory && Array.isArray(timePeriodsData)) {
            const match = timePeriodsData.find((tp) => tp?.altCaption === v.value);
            if (match) {
              originalValue = match.name;
            }
          }

          return {
            selType: v.selType || "None",
            value: originalValue,
            namedSet: false,
          };
        }),
      });
    }

    // Add named set selections criteria
    if (namedSetSelections.length > 0) {
      criteria.push({
        condition: "in",
        incExc: incExc,
        joiner: "NOOP",
        values: namedSetSelections.map((v) => {
          let originalValue = v.value;

          if (isTimePeriodCategory && Array.isArray(timePeriodsData)) {
            const match = timePeriodsData.find((tp) => tp?.altCaption === v.value);
            if (match) {
              originalValue = match.name;
            }
          }

          return {
            selType: v.selType || "None",
            value: originalValue,
            namedSet: true,
          };
        }),
      });
    }

    if (criteria.length > 0) {
      output[fullKey].push({
        filterCategory: "Selection",
        criteria,
      });
    }
  });

  return output;
};

export const deepEqual = (a: any, b: any) => {
  if (a === b) return true;
  if (
    typeof a !== "object" ||
    a === null ||
    typeof b !== "object" ||
    b === null
  ) {
    return false;
  }
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  for (let key of keysA) {
    if (!keysB.includes(key) || !deepEqual(a[key], b[key])) return false;
  }

  return true;
};

export const moveMeasuresToLast = (payload: any) => {
  if (!payload?.axes?.COLUMNS || !Array.isArray(payload.axes.COLUMNS)) {
    return payload;
  }

  const columns = payload.axes.COLUMNS;

  const nonMeasureItems: string[] = [];
  const measureItems: string[] = [];

  for (const col of columns) {
    if (col.trim().startsWith("[Measures]")) {
      measureItems.push(col);
    } else {
      nonMeasureItems.push(col);
    }
  }

  return {
    ...payload,
    axes: {
      ...payload.axes,
      COLUMNS: [...nonMeasureItems, ...measureItems],
    },
  };
};

export const countMeasureNodes = (columns: any) => {
  let count = 0;

  function traverse(nodes: any) {
    nodes.forEach((node: any) => {
      // If node has children, keep traversing
      if (node.children && node.children.length > 0) {
        traverse(node.children);
      }
      // Leaf node
      else {
        if (
          node.levelUniqueName &&
          node.levelUniqueName.includes("[Measures]")
        ) {
          count++;
        }
      }
    });
  }

  traverse(columns);
  return count;
};

export const checkIsMeasureAttr = (member: string) => {
  return member.split(".")[0] && member.split(".")[0] == "[Measures]"
    ? true
    : false;
};
